import React, { useState } from 'react';
import { useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Label, { useLabels } from "components/general/Label";
import SelectorDiscipline from "components/common/selectors/selector-discipline/selectorDiscipline";
import { Stack } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { getTeams, getAthletes } from "data/Querys";
import Loader from "components/general/Loader";
import NotAvailable from "components/common/NotAvailable";
import SelectorEvent from "components/common/selectors/SelectorEvent";
import EntriesByEventAthlete from 'components/common/entries/EntriesByEventAthlete';
import EntriesBySportAndNoc from 'components/common/entries/EntriesBySportAndNoc';
import SportEntriesAthlete from "components/common/entries/SportEntriesAthlete";
import SportEntriesTeam from "components/common/entries/SportEntriesTeam";
import { useGetH1 } from "helpers/commonCodes";
import SelectorNoc from 'components/common/selectors/selector-noc';
import { ALL_URL, SELECTOR_DISPLAY_TYPE } from 'helpers/utils';
import { useEffect } from 'react';
import { ResetFilterButton } from 'components/common/ResetFilterButton';

export default function Entries() {
    const [dataState, setDataState] = React.useState([]);
    const { mirs_competition, mirs_language, mirs_discipline, mirs_event, mirs_noc } = useUrlFriendlyToParameters();
    const [nocs, setNocs] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [events, setEvents] = useState([]);
    const [arrHasTeams, setArrHasTeams] = useState([]);
    const fLabel = useLabels()

    const dataReports = useGetH1({ mirs_competition: mirs_competition });

    useEffect(() => {
        // disciplines with team entries report
        dataReports?.h1s
            .filter(r => r.orisNo === 'C31T')
            ?.forEach((r) => {
                setArrHasTeams(prev => [...prev, r.disciplineCode]);
            })

    }, [dataReports?.h1s])

    const isTeam = arrHasTeams.includes(mirs_discipline);

    const { data: teamsDataResponse, isError: isErrorTeams, isLoading: isLoadingTeams } = useQuery({
        queryKey: ['teams', mirs_competition, mirs_language, 'A'],
        queryFn: getTeams,
        refetchInterval: 0,
        //staleTime: 5000,
        //refetchIntervalInBackground: true
    });
    const { data: athletesDataResponse, isError: isErrorAthletes, isLoading: isLoadingAthletes } = useQuery({
        queryKey: ['athletes', mirs_competition, mirs_language, 'A'],
        queryFn: getAthletes,
        refetchInterval: 0,
        //staleTime: 5000,
        //refetchIntervalInBackground: true
    });

    const isLoading = isLoadingTeams || isLoadingAthletes;
    const isError = isErrorTeams || isErrorAthletes;

    useEffect(() => {
        const teamsData = teamsDataResponse?.teams;
        const athletesData = athletesDataResponse?.persons
        let filterData = isTeam ? teamsData : athletesData; // Use data based on isTeam flag

        let tempDisciplines = new Set();
        let tempNocs = new Set();
        let tempEvents = new Set();


        // get data from teamsData
        teamsData?.forEach(dataItem => {
            if (arrHasTeams.includes(dataItem?.discipline?.code)) {

                let tempDiscipline = dataItem?.discipline?.code;
                let tempNoc = dataItem?.organisation?.code;
                let tempEventSet = new Set();

                if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
                    if (dataItem?.discipline?.code !== mirs_discipline) {
                        tempNoc = "";
                        tempEventSet.clear();
                    } else {
                        dataItem?.registeredEvents?.forEach(re => tempEventSet.add(re?.code));
                    }
                }

                if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
                    if (dataItem?.organisation?.code !== mirs_noc) {
                        tempDiscipline = "";
                        tempEventSet.clear();
                    }
                };
                if (tempNoc) {
                    tempNocs.add(tempNoc);
                }
                if (tempDiscipline) {
                    tempDisciplines.add(tempDiscipline);
                }
                tempEventSet.forEach(tempEv => tempEvents.add(tempEv));
            }
        });

        athletesData?.forEach(dataItem => {
            const hasAthletesDisciplines = dataItem?.disciplines?.some(disc => !arrHasTeams.includes(disc));
            if (hasAthletesDisciplines) {
                let tempNoc = dataItem?.organisation?.code;
                let tempEventSet = new Set();


                if (mirs_discipline && mirs_discipline !== ALL_URL.ALL_DISC) {
                    if (!dataItem?.disciplines?.some(d => d.code === mirs_discipline)) {
                        tempNoc = "";
                        tempEventSet.clear();
                    } else {
                        if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
                            if (dataItem?.organisation?.code === mirs_noc) {
                                dataItem?.registeredEvents?.forEach(re => tempEventSet.add(re?.code));
                            }
                        } else {
                            dataItem?.registeredEvents?.forEach(re => tempEventSet.add(re?.code));
                        }
                    }
                }

                dataItem?.disciplines?.forEach(d => {
                    let tempDiscipline = d.code;

                    if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
                        if (dataItem?.organisation?.code !== mirs_noc) {
                            tempDiscipline = "";
                        }
                    }
                    if (tempDiscipline) {
                        tempDisciplines.add(tempDiscipline);
                    }
                });

                if (tempNoc) {
                    tempNocs.add(tempNoc);
                }
                tempEventSet.forEach(te => tempEvents.add(te));
            }
        });
        setDisciplines([...tempDisciplines]);
        setNocs([...tempNocs]);
        setEvents([...tempEvents]);
        if (mirs_noc && mirs_noc !== ALL_URL.ALL_NOC) {
            filterData = filterData?.filter(itemOrganisation => itemOrganisation?.organisation?.code === mirs_noc);
        }
        setDataState(filterData);
    }, [teamsDataResponse, athletesDataResponse, arrHasTeams, isTeam, mirs_event, mirs_noc, mirs_discipline]);

    if (isError) {
        return <NotAvailable label="LabelNoDataAvailable" />;
    }
    if (isLoading) {
        return <Loader />;
    }

    return (
        <Tabs
            id="mirs-navsport"
            defaultActiveKey="tab-entries"
            className="mb-3 mirs-tab-result"
            aria-label={fLabel({ data: "LabelEntries" })}
        >
            <Tab eventKey="tab-entries" title={(<Label data='LabelEntries' />)} >
                <Stack direction="horizontal" className='selectors-container'>
                    <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="entries" disciplinesList={disciplines} useDisciplinesList={true} displayAllDisc={false} />
                    <SelectorNoc display_type={SELECTOR_DISPLAY_TYPE.COMBO} page='entries' nocsList={nocs} useNocList={true} />
                    <SelectorEvent page='entries' eventList={events} useEventList={true} />
                    <ResetFilterButton page='entries' />
                </Stack>
                {isTeam
                    ? <SportEntriesTeam data={dataState} />
                    : (mirs_event)
                        ? <EntriesByEventAthlete data={dataState} />
                        : <SportEntriesAthlete data={dataState} />
                }
            </Tab>
            <Tab eventKey="tab-entries-by-noc" title={(<Label data='LabelEntriesByNoc' />)} >
                <Stack direction="horizontal" className='selectors-container'>
                    <SelectorDiscipline display_type={SELECTOR_DISPLAY_TYPE.COMBO} page="entries" disciplinesList={disciplines} useDisciplinesList={true} displayAllDisc={false} />
                    <SelectorNoc display_type={SELECTOR_DISPLAY_TYPE.COMBO} page='entries' nocsList={nocs} useNocList={true} />
                    <ResetFilterButton page='entries' />
                </Stack>
                <EntriesBySportAndNoc data={dataState} isTeam={isTeam} />
            </Tab>
        </Tabs>
    )
};
